/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars*/
import {
  initialize,
  type LDContext,
  type LDClient,
  type LDOptions,
} from 'launchdarkly-js-client-sdk';
import { useEffect, useState } from 'react';

import { proxyFlags, mergeFlags, mergeVariation } from './base.js';
import { makeQueueable } from './queue.js';

// Notion doc: https://www.notion.so/curology/How-To-Use-LaunchDarkly-Feature-Flags-951fa6eb639d4ceca0a3cfa346d1179c
let ldSingletonClient: LDClient;

export const buildClientFeatureFlags = <
  Flags extends { [key: string]: unknown },
  Events extends string | number,
  Params extends { [key in Events]?: unknown },
  JSONFlagValues extends (keyof Flags)[] = (keyof Flags)[],
  NeededParams = Events extends keyof Params ? Params[Events] : never,
>({
  defaultFlagValues,
  enabled,
  jsonFlagValues,
}: {
  /**
   * A record of default values for flags. If no default is provided
   * for a flag, `false` will be the default.
   */
  defaultFlagValues?: Partial<Flags>;
  /**
   * Should usually be `typeof window !== 'undefined'`. This has to be passed
   * from the Next.js app and can't be computed in this library because of a
   * limitation of how Next is compiled.
   */
  enabled: boolean;
  /**
   * A list of flag names that should be automatically parsed as
   * an object from a JSON string.
   */
  jsonFlagValues?: JSONFlagValues;
}) => {
  type UseFlagsType = <Name extends keyof Flags = keyof Flags>(
    name: Name,
  ) => Flags[Name] | false;

  const getFlagDefaults = () => proxyFlags<Flags>(defaultFlagValues);
  const getFlagDefault: UseFlagsType = (name) =>
    defaultFlagValues?.[name] ?? false;

  const clientSideId = process.env.NEXT_PUBLIC_LAUNCH_DARKLY_ID;
  if (enabled && !clientSideId) {
    console.error(
      'Not initializing feature flags because NEXT_PUBLIC_LAUNCH_DARKLY_ID was not provided.',
    );
  }

  if (!enabled || !clientSideId) {
    return {
      initLd: async (
        context: LDContext,
        options?: LDOptions,
      ): Promise<void> => {},
      useFlags: getFlagDefaults,
      useFlag: getFlagDefault,
      trackFeature: (
        evt: Events | `${Events}`,
        params?: NeededParams,
      ): void => {},
      identifyContext: (ldContext: LDContext): void => {},
      flushFeatureTracking(callback: () => void): void {},
      closeFeatureFlagClient(callback: () => void): void {},
    };
  }

  const [trackFeature, flushTrackFeatureQueue] = makeQueueable(
    (evt: Events | `${Events}`, params?: NeededParams) =>
      ldSingletonClient.track(`${evt}`, params || undefined),
  );

  const [identifyContext, flushIdentifyContextQueue] = makeQueueable(
    (ldContext: LDContext) => ldSingletonClient.identify(ldContext),
  );

  const [onReady, flushOnReadyQueue] = makeQueueable((callback: () => void) =>
    callback(),
  );

  const initLd = async (context: LDContext, options?: LDOptions) => {
    if (ldSingletonClient) {
      return;
    }

    ldSingletonClient = initialize(clientSideId, context, {
      sendEventsOnlyForVariation: true,
      ...options,
    });

    identifyContext(context);

    try {
      await ldSingletonClient.waitForInitialization();
      flushIdentifyContextQueue();
      flushTrackFeatureQueue();
      flushOnReadyQueue();
    } catch (exception) {
      console.error(`LaunchDarkly client reported error: "${exception}"`);
    }
  };

  const useFlags = () => {
    const flagDefaults = getFlagDefaults();
    const [flags, setFlags] = useState(flagDefaults);

    useEffect(() => {
      onReady(() =>
        setFlags(
          mergeFlags<Flags>(
            flagDefaults,
            ldSingletonClient?.allFlags() || {},
            jsonFlagValues as string[],
          ),
        ),
      );
    }, []);

    return flags;
  };

  const useFlag: UseFlagsType = (name) => {
    const flagDefault = getFlagDefault(name);
    const [flag, setFlag] = useState(flagDefault);

    const getVariation = (name: string) =>
      mergeVariation(
        flagDefault,
        { name, variation: ldSingletonClient?.variation(name) },
        jsonFlagValues as string[],
      );

    useEffect(() => {
      onReady(() => setFlag(getVariation(name as string)));
    }, []);

    return flag;
  };

  const flushFeatureTracking = (callback: () => void) => {
    onReady(async () => {
      await ldSingletonClient.flush();
      callback();
    });
  };

  const closeFeatureFlagClient = (callback: () => void) => {
    onReady(async () => {
      await ldSingletonClient.close();
      callback();
    });
  };

  return {
    initLd,
    useFlags,
    useFlag,
    trackFeature,
    identifyContext,
    flushFeatureTracking,
    closeFeatureFlagClient,
  };
};
